import React from 'react';

const BRANCHES = [
  { slug: 'niko1', name: 'Niko 1' }, { slug: 'niko2', name: 'Niko 2' }, { slug: 'niko3', name: 'Niko 3' },
  { slug: 'niko4', name: 'Niko 4' }, { slug: 'niko5', name: 'Niko 5' }, { slug: 'niko7', name: 'Niko 7' },
];
const SITE_ORIGIN = 'https://veo360.ai';

interface Prize { id: string; name: string; }
interface Entry { nombre: string; dni: string; whatsapp: string; ip?: string; vendedor?: string; categoria?: string; resultado: string; premio: string; fecha: string; }
interface VendedorStat { vendedor: string; total: number; wins: number; }
interface CategoriaStat { categoria: string; total: number; aciertos: number; }
interface CampaignSummary {
  id: string; name: string; createdAt: string; active: boolean;
  totalAttempts: number; used: number; left: number; wins: number;
  prizes: Prize[]; ipLimitEnabled: boolean; ipLimitPerDay: number;
  vendedores: VendedorStat[]; categorias: CategoriaStat[];
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('pc_token') || '';
  return token ? { Authorization: 'Bearer ' + token } : {};
}
async function api(path: string, opts: any = {}) {
  const headers = { 'Content-Type': 'application/json', ...authHeader(), ...(opts.headers || {}) };
  const res = await fetch('/ruleta/api/trivia' + path, {
    method: opts.method || 'GET', headers, body: opts.body ? JSON.stringify(opts.body) : undefined
  });
  let data: any = null;
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) throw new Error((data && data.error) || 'Error');
  return data;
}

export default function TriviaPanel() {
  const [branch, setBranch] = React.useState(BRANCHES[0].slug);
  const [tab, setTab] = React.useState<'stats' | 'config' | 'entries' | 'links'>('stats');
  const [campaigns, setCampaigns] = React.useState<CampaignSummary[]>([]);
  const [viewId, setViewId] = React.useState('');
  const [summary, setSummary] = React.useState<CampaignSummary | null>(null);
  const [entries, setEntries] = React.useState<Entry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [saveMsg, setSaveMsg] = React.useState('');
  const [search, setSearch] = React.useState('');

  const [cfgName, setCfgName] = React.useState('');
  const [cfgTotal, setCfgTotal] = React.useState(500);
  const [cfgPrizes, setCfgPrizes] = React.useState<Prize[]>([]);
  const [cfgIpEnabled, setCfgIpEnabled] = React.useState(false);
  const [cfgIpPerDay, setCfgIpPerDay] = React.useState(5);

  const [showNew, setShowNew] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newTotal, setNewTotal] = React.useState(500);
  const [creating, setCreating] = React.useState(false);

  const [linkVendedor, setLinkVendedor] = React.useState('');
  const [linkCopied, setLinkCopied] = React.useState(false);

  const loadList = React.useCallback(async (b: string, preferId?: string) => {
    setLoading(true); setLoadError('');
    try {
      const list: CampaignSummary[] = await api(`/${b}/admin/campaigns`);
      setCampaigns(list);
      const active = list.find(c => c.active);
      const chosen = preferId && list.find(c => c.id === preferId) ? preferId : (active ? active.id : (list[0] ? list[0].id : ''));
      setViewId(chosen);
      if (chosen) {
        const s: CampaignSummary = await api(`/${b}/admin/campaigns/${chosen}/summary`);
        const e: Entry[] = await api(`/${b}/admin/campaigns/${chosen}/entries`);
        setSummary(s); setEntries(e);
        setCfgName(s.name); setCfgTotal(s.totalAttempts); setCfgPrizes(s.prizes);
        setCfgIpEnabled(s.ipLimitEnabled); setCfgIpPerDay(s.ipLimitPerDay);
      } else { setSummary(null); setEntries([]); }
    } catch (e) {
      setLoadError('No se pudo cargar la sucursal. Probá recargar la página o volver a iniciar sesión.');
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { loadList(branch); }, [branch, loadList]);

  const loadDetail = async (id: string) => {
    setLoading(true);
    try {
      const s: CampaignSummary = await api(`/${branch}/admin/campaigns/${id}/summary`);
      const e: Entry[] = await api(`/${branch}/admin/campaigns/${id}/entries`);
      setSummary(s); setEntries(e); setViewId(id);
      setCfgName(s.name); setCfgTotal(s.totalAttempts); setCfgPrizes(s.prizes);
      setCfgIpEnabled(s.ipLimitEnabled); setCfgIpPerDay(s.ipLimitPerDay);
    } catch (e) {}
    setLoading(false);
  };

  const activateCampaign = async (id: string) => {
    try { await api(`/${branch}/admin/campaigns/${id}/activate`, { method: 'POST' }); loadList(branch, id); }
    catch (e) { alert('No se pudo activar la campaña.'); }
  };

  const createCampaign = async () => {
    if (!newName.trim()) { alert('Ponele un nombre a la campaña.'); return; }
    setCreating(true);
    try {
      const created = await api(`/${branch}/admin/campaigns`, { method: 'POST', body: { name: newName.trim(), totalAttempts: newTotal } });
      setShowNew(false); setNewName(''); setNewTotal(500);
      await loadList(branch, created.id);
    } catch (e) { alert('No se pudo crear la campaña.'); }
    setCreating(false);
  };

  const saveConfig = async () => {
    try {
      await api(`/${branch}/admin/campaigns/${viewId}/config`, {
        method: 'POST',
        body: { name: cfgName, prizes: cfgPrizes.filter(p => p.name.trim()), totalAttempts: cfgTotal, ipLimitEnabled: cfgIpEnabled, ipLimitPerDay: cfgIpPerDay }
      });
      setSaveMsg('Guardado. Si cambiaste el total de cupos, ya se aplica sin necesidad de reiniciar.');
      setTimeout(() => setSaveMsg(''), 6000);
      loadList(branch, viewId);
    } catch (e) { setSaveMsg('No se pudo guardar.'); }
  };

  const resetCampaign = async () => {
    if (!summary) return;
    if (!window.confirm(`Esto borra todos los participantes de "${summary.name}" y reinicia el contador de cupos. ¿Confirmás?`)) return;
    try { await api(`/${branch}/admin/campaigns/${viewId}/reset`, { method: 'POST' }); loadList(branch, viewId); }
    catch (e) { alert('No se pudo reiniciar.'); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`/ruleta/api/trivia/${branch}/admin/campaigns/${viewId}/export.csv`, { headers: authHeader() });
      if (!res.ok) throw new Error('Error');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `trivia-${branch}-${(summary?.name || 'campana')}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert('No se pudo exportar el CSV.'); }
  };

  const filteredEntries = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return e.nombre.toLowerCase().includes(q) || e.dni.includes(q) || e.whatsapp.includes(q) || (e.vendedor || '').toLowerCase().includes(q) || (e.categoria || '').toLowerCase().includes(q);
  }).slice().reverse();

  const inputStyle: React.CSSProperties = { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' };
  const cardStyle: React.CSSProperties = { background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' };

  const branchLabel = BRANCHES.find(b => b.slug === branch)?.name || branch;
  const baseLink = `${SITE_ORIGIN}/trivia/?s=${branch}`;
  const vendedorLink = linkVendedor.trim() ? `${baseLink}&v=${encodeURIComponent(linkVendedor.trim())}` : baseLink;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(vendedorLink)}`;
  const waText = encodeURIComponent(`¡Probá la Trivia de Niko ${branchLabel}! 🧠 Girá, respondé y ganá: ${vendedorLink}`);
  const waLink = `https://wa.me/?text=${waText}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(vendedorLink).then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); });
  };

  return (
    <div style={{ padding: '24px', color: '#fff', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0 }}>🧠 Trivia</h2>
        <select value={branch} onChange={e => setBranch(e.target.value)} style={inputStyle}>
          {BRANCHES.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
        </select>
      </div>

      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '220px' }}>
          <label style={{ fontSize: '12px', color: '#94a3b8' }}>Campaña:</label>
          <select value={viewId} onChange={e => loadDetail(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}{c.active ? ' (activa)' : ''}</option>)}
          </select>
        </div>
        {summary && !summary.active && (
          <button onClick={() => activateCampaign(viewId)} style={{ background: '#4ade80', color: '#052e16', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
            Marcar como activa
          </button>
        )}
        <button onClick={() => setShowNew(!showNew)} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
          + Nueva campaña
        </button>
      </div>

      {showNew && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, fontSize: '15px' }}>Nueva campaña de trivia</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>Nombre</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} style={{ ...inputStyle, minWidth: '220px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>Cupos totales</label>
              <input type="number" value={newTotal} onChange={e => setNewTotal(parseInt(e.target.value) || 0)} style={inputStyle} />
            </div>
            <button onClick={createCampaign} disabled={creating} style={{ background: '#4ade80', color: '#052e16', border: 'none', borderRadius: '8px', padding: '9px 18px', fontWeight: 700, cursor: 'pointer' }}>
              {creating ? 'Creando...' : 'Crear y activar'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px' }}>
            A diferencia de la ruleta, acá no hay % de premios: gana quien conteste bien, hasta agotar los cupos.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {(['stats', 'config', 'entries', 'links'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab === t ? '#3b82f6' : '#1e293b', color: '#fff', border: 'none',
            borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600
          }}>
            {t === 'stats' ? 'Resumen' : t === 'config' ? 'Configuración' : t === 'entries' ? 'Participantes' : 'Enlaces y QR'}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: '#64748b', padding: '20px' }}>Cargando...</div>}
      {!loading && loadError && <div style={{ color: '#f87171', padding: '20px' }}>{loadError}</div>}

      {!loading && !loadError && tab === 'stats' && summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'Cupos totales', val: summary.totalAttempts },
              { label: 'Cupos usados', val: summary.used },
              { label: 'Cupos restantes', val: summary.left },
              { label: 'Premios entregados', val: summary.wins, win: true },
            ].map(k => (
              <div key={k.label} style={cardStyle}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: k.win ? '#4ade80' : '#e2e8f0' }}>{k.val}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Aciertos por categoría</h3>
            {summary.categorias.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '13px' }}>Todavía no hay participaciones.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: '#94a3b8' }}>Categoría</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8' }}>Jugadas</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8' }}>Aciertos</th>
                </tr></thead>
                <tbody>
                  {summary.categorias.map(c => (
                    <tr key={c.categoria} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={{ padding: '6px 10px', color: '#e2e8f0', fontWeight: 600 }}>{c.categoria}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#e2e8f0' }}>{c.total}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#4ade80' }}>{c.aciertos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Fidelización por vendedor</h3>
            {summary.vendedores.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '13px' }}>Generá un link con nombre de vendedor en "Enlaces y QR" para medirlo.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: '#94a3b8' }}>Vendedor</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8' }}>Clientes</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8' }}>Ganadores</th>
                </tr></thead>
                <tbody>
                  {summary.vendedores.map(v => (
                    <tr key={v.vendedor} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={{ padding: '6px 10px', color: '#e2e8f0', fontWeight: 600 }}>{v.vendedor}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#e2e8f0' }}>{v.total}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#4ade80' }}>{v.wins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!loading && !loadError && tab === 'config' && summary && (
        <>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Nombre de la campaña</h3>
            <input value={cfgName} onChange={e => setCfgName(e.target.value)} style={{ ...inputStyle, width: '320px' }} />
          </div>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Cupos totales</h3>
            <input type="number" value={cfgTotal} onChange={e => setCfgTotal(parseInt(e.target.value) || 0)} style={inputStyle} />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px' }}>Cuántas personas en total pueden participar de esta campaña.</div>
          </div>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Premios (se sortean entre quienes acierten)</h3>
            {cfgPrizes.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input value={p.name} onChange={e => { const next = [...cfgPrizes]; next[i] = { ...next[i], name: e.target.value }; setCfgPrizes(next); }} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => setCfgPrizes(cfgPrizes.filter((_, idx) => idx !== i))} style={{ background: '#374151', color: '#f87171', border: 'none', borderRadius: '8px', padding: '0 12px', cursor: 'pointer' }}>✕</button>
              </div>
            ))}
            <button onClick={() => setCfgPrizes([...cfgPrizes, { id: 'p' + Date.now(), name: '' }])} style={{ background: 'transparent', color: '#60a5fa', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>+ Agregar premio</button>
          </div>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>Límite por IP (adicional al DNI único)</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input type="checkbox" checked={cfgIpEnabled} onChange={e => setCfgIpEnabled(e.target.checked)} />
              <span style={{ fontSize: '13px' }}>Activar límite por IP</span>
            </div>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>Jugadas máximas por IP por día</label>
            <input type="number" value={cfgIpPerDay} onChange={e => setCfgIpPerDay(parseInt(e.target.value) || 5)} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={saveConfig} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>Guardar configuración</button>
            <span style={{ fontSize: '13px', color: '#4ade80' }}>{saveMsg}</span>
          </div>
          <div style={{ ...cardStyle, border: '1px dashed #b4472f', marginTop: '16px' }}>
            <h3 style={{ marginTop: 0, fontSize: '15px', color: '#f87171' }}>Reiniciar esta campaña</h3>
            <p style={{ fontSize: '12px', color: '#94a3b8' }}>Borra todos los participantes de "{summary.name}" y reinicia el contador de cupos.</p>
            <button onClick={resetCampaign} style={{ background: '#b4472f', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontWeight: 600, cursor: 'pointer' }}>Reiniciar y borrar participantes</button>
          </div>
        </>
      )}

      {!loading && !loadError && tab === 'entries' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <input placeholder="Buscar por nombre, DNI, vendedor o categoría..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
            <button onClick={exportCsv} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Exportar CSV</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ borderBottom: '1px solid #334155' }}>
                {['Nombre', 'DNI', 'WhatsApp', 'Vendedor', 'Categoría', 'Resultado', 'Premio', 'Fecha'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredEntries.length === 0 && <tr><td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Sin participantes todavía.</td></tr>}
                {filteredEntries.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 10px', color: '#e2e8f0' }}>{e.nombre}</td>
                    <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{e.dni}</td>
                    <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{e.whatsapp}</td>
                    <td style={{ padding: '8px 10px', color: '#e2e8f0' }}>{e.vendedor || '—'}</td>
                    <td style={{ padding: '8px 10px', color: '#e2e8f0' }}>{e.categoria || '—'}</td>
                    <td style={{ padding: '8px 10px' }}><span style={{ color: e.resultado === 'Ganó' ? '#4ade80' : '#94a3b8', fontWeight: 600 }}>{e.resultado}</span></td>
                    <td style={{ padding: '8px 10px', color: '#e2e8f0' }}>{e.premio || '—'}</td>
                    <td style={{ padding: '8px 10px', color: '#64748b' }}>{new Date(e.fecha).toLocaleString('es-AR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'links' && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, fontSize: '15px' }}>Generar enlace para {branchLabel}</h3>
          <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5, marginBottom: '14px' }}>
            El link juega contra la campaña <b style={{ color: '#e2e8f0' }}>activa</b> de esta sucursal.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>Nombre del vendedor (opcional)</label>
            <input value={linkVendedor} onChange={e => setLinkVendedor(e.target.value)} placeholder="Ej: Martina" style={{ ...inputStyle, width: '260px' }} />
          </div>
          <div style={{ background: '#0f172a', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#93c5fd', wordBreak: 'break-all', marginBottom: '10px' }}>{vendedorLink}</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button onClick={copyLink} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>{linkCopied ? '✓ Copiado' : 'Copiar enlace'}</button>
            <a href={waLink} target="_blank" rel="noreferrer" style={{ background: '#25D366', color: '#052e16', borderRadius: '8px', padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '13px', textDecoration: 'none' }}>Enviar por WhatsApp</a>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Código QR</div>
            <img src={qrUrl} alt="QR de la trivia" style={{ background: '#fff', padding: '10px', borderRadius: '10px', width: '180px', height: '180px' }} />
            <div style={{ marginTop: '8px' }}><a href={qrUrl} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: '12px' }}>Abrir imagen en grande / descargar</a></div>
          </div>
        </div>
      )}
    </div>
  );
}
